import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import InvitationPreviewV2 from '../_components/InvitationPreview/InvitationPreviewV2';
import { INVITATION_TEMPLATES_V2 } from '../_data/invitation-templates-v2';
import { InvitationDesign } from '../_types/invitation-design-v2';
import { Event } from '../_graphql/generated/operations';

// The proof sheet a customer opens from one unguessable link. An agent puts a
// handful of candidate designs in front of them, they browse and either pick
// one or ask for changes; a new round REPLACES the current view but every
// earlier round stays reachable, because the thing they liked two rounds ago
// must never become unreachable.
//
// It is deliberately data-free: no GraphQL, no api-client, no logged-in user.
// The whole proof is one static JSON document under /proofs/<id>.json, so the
// link works for someone who has no account and never will.

interface ProofBriefItem {
  label: string;
  value: string;
}

interface ProofRound {
  note?: string;
  designs: InvitationDesign[];
}

interface Proof {
  id: string;
  title?: string;
  brief?: ProofBriefItem[];
  rounds: ProofRound[];
}

// The renderer needs an Event; nothing here has one, and the template content
// is written against the placeholder variables rather than real guest data.
const stubEvent = {
  id: 'proof',
  eventName: 'Your Event',
  eventDateTime: Date.now(),
  location: 'Your Venue',
} as unknown as Event;

// The card is a fixed 528x720 at design size. Scaling it with a transform
// inside a clipping wrapper keeps the layout identical to the real thing —
// re-laying out at a smaller width would silently change line breaks. 0.6
// still shows a subtle weave; 0.5 loses it.
const CARD_W = 528;
const CARD_H = 720;
const SCALE = 0.6;

const styles = `
.proof {
  --proof-bg: #f4f3f0;
  --proof-panel: #ffffff;
  --proof-ink: #1c1c1a;
  --proof-muted: #6b6a66;
  --proof-line: #dedcd6;
  --proof-accent: #2f4f43;
  --proof-accent-ink: #ffffff;
  --proof-focus: #2f4f43;
  min-height: 100vh;
  background: var(--proof-bg);
  color: var(--proof-ink);
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  padding: 40px 24px 96px;
}
@media (prefers-color-scheme: dark) {
  .proof {
    --proof-bg: #17181a;
    --proof-panel: #202225;
    --proof-ink: #ececea;
    --proof-muted: #9a9a96;
    --proof-line: #33363b;
    --proof-accent: #9fbcab;
    --proof-accent-ink: #14201b;
    --proof-focus: #9fbcab;
  }
}
.proof__inner { max-width: 1180px; margin: 0 auto; }
.proof__eyebrow {
  font-size: 11px; letter-spacing: 2px; text-transform: uppercase;
  color: var(--proof-muted); margin: 0 0 8px;
}
.proof__title { font-size: 22px; font-weight: 600; margin: 0 0 16px; letter-spacing: -0.01em; }
.proof__brief { display: flex; flex-wrap: wrap; gap: 8px 24px; margin: 0 0 40px; padding: 0; list-style: none; }
.proof__brief li { font-size: 13px; color: var(--proof-muted); }
.proof__brief b { font-weight: 500; color: var(--proof-ink); }
.proof__roundbar {
  display: flex; align-items: baseline; flex-wrap: wrap; gap: 12px 16px;
  padding-bottom: 16px; margin-bottom: 32px; border-bottom: 1px solid var(--proof-line);
}
.proof__roundlabel { font-size: 13px; font-weight: 600; letter-spacing: 0.02em; }
.proof__note { font-size: 13px; color: var(--proof-muted); flex: 1 1 260px; line-height: 1.6; }
.proof__nav { display: flex; gap: 8px; }
.proof__btn {
  font: inherit; font-size: 13px; line-height: 1; padding: 8px 14px;
  background: var(--proof-panel); color: var(--proof-ink);
  border: 1px solid var(--proof-line); border-radius: 999px; cursor: pointer;
}
.proof__btn:hover:not(:disabled) { border-color: var(--proof-muted); }
.proof__btn:disabled { opacity: 0.4; cursor: default; }
.proof__grid {
  display: grid; gap: 40px 32px;
  grid-template-columns: repeat(auto-fill, minmax(${CARD_W * SCALE}px, ${CARD_W * SCALE}px));
  justify-content: center;
}
.proof__card {
  display: block; padding: 0; border: 0; background: none; font: inherit;
  text-align: left; cursor: pointer; color: inherit;
}
.proof__frame {
  width: ${CARD_W * SCALE}px; height: ${CARD_H * SCALE}px;
  overflow: hidden; position: relative;
  border-radius: 3px; background: var(--proof-panel);
  box-shadow: 0 1px 2px rgba(0,0,0,0.10), 0 10px 28px rgba(0,0,0,0.12);
  outline: 2px solid transparent; outline-offset: 4px;
  transition: outline-color 140ms ease, box-shadow 140ms ease;
}
.proof__card:hover .proof__frame { box-shadow: 0 1px 2px rgba(0,0,0,0.12), 0 16px 36px rgba(0,0,0,0.18); }
.proof__card:focus-visible .proof__frame { outline-color: var(--proof-focus); }
.proof__card[aria-selected="true"] .proof__frame { outline-color: var(--proof-accent); }
.proof__scaler {
  width: ${CARD_W}px; height: ${CARD_H}px;
  transform: scale(${SCALE}); transform-origin: top left;
  position: absolute; top: 0; left: 0;
  pointer-events: none;
}
.proof__caption {
  margin-top: 12px; font-size: 12px; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--proof-muted);
}
.proof__caption b { font-weight: 600; letter-spacing: 0.06em; color: var(--proof-ink); }
.proof__card[aria-selected="true"] .proof__caption b::after {
  content: ' · selected'; color: var(--proof-accent); font-weight: 500;
}
.proof__reply {
  position: sticky; bottom: 0; margin-top: 48px;
  background: var(--proof-panel); border: 1px solid var(--proof-line);
  border-radius: 8px; padding: 16px 20px;
  display: flex; flex-wrap: wrap; align-items: center; gap: 12px 16px;
}
.proof__replytext { font-size: 13px; color: var(--proof-muted); flex: 1 1 240px; }
.proof__line {
  display: block; margin-top: 6px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px; color: var(--proof-ink); word-break: break-word;
}
.proof__copy {
  font: inherit; font-size: 13px; line-height: 1; padding: 10px 16px;
  background: var(--proof-accent); color: var(--proof-accent-ink);
  border: 0; border-radius: 999px; cursor: pointer;
}
.proof__copystatus { font-size: 12px; color: var(--proof-muted); }
.proof__empty { font-size: 14px; color: var(--proof-muted); max-width: 62ch; line-height: 1.7; margin: 0 0 40px; }
@media (prefers-reduced-motion: reduce) {
  .proof *, .proof *::before, .proof *::after { transition: none !important; animation: none !important; }
}
`;

const ProofSheet: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [proof, setProof] = useState<Proof | null>(null);
  const [loading, setLoading] = useState(true);
  const [roundIndex, setRoundIndex] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // A missing proof is NOT an error page — it is the no-inspiration entry
    // path, and it lands on the default gallery below.
    fetch(`/proofs/${encodeURIComponent(id ?? '')}.json`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: Proof | null) => {
        if (cancelled) return;
        setProof(data && Array.isArray(data.rounds) ? data : null);
        setRoundIndex(data && Array.isArray(data.rounds) ? Math.max(0, data.rounds.length - 1) : 0);
      })
      .catch(() => {
        if (!cancelled) setProof(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const rounds = proof?.rounds ?? [];
  const hasRounds = rounds.length > 0;
  const round = hasRounds ? rounds[Math.min(roundIndex, rounds.length - 1)] : undefined;

  const designs: InvitationDesign[] = useMemo(
    () => (round ? round.designs : INVITATION_TEMPLATES_V2),
    [round],
  );

  const selected = designs.find((d) => d.template.id === selectedId);
  const replyLine = selected
    ? `Use ${selected.metadata?.name ?? selected.template.name} (${selected.template.id})`
    : '';

  const copy = useCallback(() => {
    if (!replyLine) return;
    try {
      // No clipboard permission, an insecure origin, or a browser that simply
      // does not have the API — all land here, and the line is on screen to be
      // selected by hand either way.
      const write = navigator.clipboard?.writeText(replyLine);
      if (write && typeof write.then === 'function') {
        write.then(() => setCopyState('copied')).catch(() => setCopyState('failed'));
      } else {
        setCopyState('failed');
      }
    } catch {
      setCopyState('failed');
    }
  }, [replyLine]);

  useEffect(() => setCopyState('idle'), [replyLine]);

  // Changing round clears the selection: a card the customer picked in round 1
  // is not the card they are looking at now.
  const goTo = (next: number) => {
    setRoundIndex(next);
    setSelectedId(null);
  };

  if (loading) {
    return (
      <div className="proof">
        <style>{styles}</style>
        <div className="proof__inner">
          <p className="proof__empty">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="proof">
      <style>{styles}</style>
      <div className="proof__inner">
        <p className="proof__eyebrow">Proof sheet</p>
        <h1 className="proof__title">{proof?.title ?? 'A place to start'}</h1>

        {proof?.brief?.length ? (
          <ul className="proof__brief">
            {proof.brief.map((item) => (
              <li key={item.label}>
                {item.label}: <b>{item.value}</b>
              </li>
            ))}
          </ul>
        ) : null}

        {hasRounds && round ? (
          <div className="proof__roundbar">
            <span className="proof__roundlabel">
              Round {Math.min(roundIndex, rounds.length - 1) + 1} of {rounds.length}
            </span>
            {round.note ? <span className="proof__note">{round.note}</span> : null}
            <span className="proof__nav">
              <button
                type="button"
                className="proof__btn"
                onClick={() => goTo(roundIndex - 1)}
                disabled={roundIndex <= 0}
              >
                ← Back
              </button>
              <button
                type="button"
                className="proof__btn"
                onClick={() => goTo(roundIndex + 1)}
                disabled={roundIndex >= rounds.length - 1}
              >
                Forward →
              </button>
            </span>
          </div>
        ) : (
          <p className="proof__empty">
            Nothing has been drawn for you yet, so this is the whole library to browse. Pick anything
            that feels close and say so — the next round will be built around it.
          </p>
        )}

        <div className="proof__grid" role="listbox" aria-label="Candidate designs">
          {designs.map((design) => {
            const isSelected = design.template.id === selectedId;
            const toggle = () => setSelectedId(isSelected ? null : design.template.id);
            return (
              // A div rather than a <button>: the preview is flow content and a
              // button may only contain phrasing content. Keyboard parity is
              // restored explicitly below.
              <div
                key={design.template.id}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                className="proof__card"
                onClick={toggle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle();
                  }
                }}
              >
                <div className="proof__frame">
                  <div className="proof__scaler">
                    <InvitationPreviewV2 design={design} event={stubEvent} />
                  </div>
                </div>
                <div className="proof__caption">
                  <b>{design.metadata?.name ?? design.template.name}</b>
                  {' · '}
                  {design.template.category}
                </div>
              </div>
            );
          })}
        </div>

        {selected ? (
          <div className="proof__reply">
            <span className="proof__replytext">
              Send this back and the next round starts from it:
              <code className="proof__line">{replyLine}</code>
            </span>
            <button type="button" className="proof__copy" onClick={copy}>
              Copy
            </button>
            <span className="proof__copystatus" role="status">
              {copyState === 'copied' ? 'Copied' : null}
              {copyState === 'failed' ? 'Copy unavailable — select the line above' : null}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProofSheet;
